import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { queryClient } from '@/queryClient.js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { Station } from '@/models/station.js'
import {
	PASSPORT_DATE_PATHS,
} from './stationPassport.js'
import {
	RESOURCE_NAMES,
	readResources,
	parseResource,
} from '@/api/api.js'



/**
 * Many station passports.
 * @param {ResourceId | null} organizationId - id of organization or null for all stations of all organizations
 * @param {Array of ResourceId | null} departmentsId - Array of id of organization departments or null for all departments
 * * @param {Array of ResourceId | null} sectorsId - Array of id of organization sectors or null for all sectors
 */
export function useStationsPassport(organizationId=null, departmentsId=null, sectorsId=null) {
// export function useStationsPassport() {
	const accountStore = useAccontStore()

	const orgId = ref(null)
	const depsId = ref(null)
	const sectsId = ref(null)

	setFilters({
		organizationId: organizationId,
		departmentsId: departmentsId,
		sectorsId: sectorsId,
	})

	const stationsQuery = useQuery({
		// Include auth identity and time filter in key to auto-refetch on change
		queryKey: computed(() => [
			RESOURCE_NAMES.stationPassports,
			orgId,
			...(depsId.value == null ? [] : depsId.value),
			...(sectsId.value == null ? [] : sectsId.value),
		]),
		queryFn: async () => {
			let whereObject = {}
			let args = []

			if (orgId.value !== null) {
				whereObject[Station.passportKeys.orgId] = orgId.value
			}

			if (depsId.value !== null
				&& Array.isArray(depsId.value)
				&& depsId.value.length > 0
			) {
				whereObject[Station.passportKeys.departmentId] = {
					"$in": depsId.value,
				}
			}

			if (sectsId.value !== null
				&& Array.isArray(sectsId.value)
				&& sectsId.value.length > 0
			) {
				whereObject[Station.passportKeys.sectorId] = {
					"$in": sectsId.value,
				}
			}

			args = [ `where=${JSON.stringify(whereObject)}` ]
			
			// Read all stations with filtering
			const res = await readResources(
				RESOURCE_NAMES.stationPassports,
				accountStore.userLogin,
				accountStore.userPassword,
				args,
			)

			return res.map((el) => parseResource(el, PASSPORT_DATE_PATHS))
		},
		enabled: true,
		// Keep results warm to reduce repeated network requests.
		staleTime: 60 * 60 * 1000,
	})

	const allPassports = computed(() => stationsQuery.data.value ?? [])
	const passportsMap = computed(() => new Map(
		allPassports.value.map(
			(station) => new Array(station[Station.passportKeys.id], structuredClone(station))
		)
	))

	// Set period or alert type filters
	function setFilters(
		filters={
			organizationId: null,
			departmentsId: null,
			sectorsId: null,
		}
	) {
		if (filters.organizationId !== null
			&& (
				typeof filters.organizationId === 'string'
				|| filters.organizationId instanceof String
			)
		) {
			orgId.value = filters.organizationId.slice(0)
		}

		if (filters.departmentsId !== null
			&& Array.isArray(filters.departmentsId)
			&& filters.departmentsId.length > 0
		) {
			depsId.value = [...filters.departmentsId]
		}

		if (filters.sectorsId !== null
			&& Array.isArray(filters.sectorsId)
			&& filters.sectorsId.length > 0
		) {
			sectsId.value = [...filters.sectorsId]
		}
	}

	// Update all passports now
	async function updateNow() {
		await queryClient.refetchQueries({
			queryKey: [RESOURCE_NAMES.stationPassports, ],
			exact: false,
		})
}

	return {
		passportsMap,

		isLoading: stationsQuery.isLoading,
		isError: stationsQuery.isError,
		error: stationsQuery.error,

		setFilters,
		updateNow,
	}
}